/**
 * The frame loop's handle on the HUD and the floating OPEN button.
 *
 * These two surfaces are written EVERY FRAME (target under the cursor, a 3D
 * anchor projected to page coordinates), so they cannot go through React
 * state - the components register imperative handles here at mount, and the
 * loop writes DOM through them. This is the one sanctioned bridge between
 * the frame loop and React-owned chrome; everything slower goes through the
 * observer stores.
 */

import type { Tool } from '../../fab/layout'

/**
 * The approach card's four lines, already in the student's language. A
 * tool's card is composed by the HUD from the Tool; a vehicle's is composed
 * by the floor each frame from the vehicle's live status, so it changes as
 * the vehicle does.
 */
export interface HudCard {
  tag: string
  name: string
  blurb: string
  /** Locale-only markup (<kbd>), like the tool card's key line. */
  keyHtml: string
}

export interface HudHandle {
  setTarget(tool: Tool | null): void
  setCard(card: HudCard | null): void
  setStatus(parts: Array<[string, string]>): void
}

export interface OpenButtonHandle {
  /** Place and show the button at page coordinates. */
  show(x: number, y: number): void
  hide(): void
}

let hud: HudHandle | null = null
let openButton: OpenButtonHandle | null = null

export const hudBridge = {
  bind(handle: HudHandle | null): void {
    hud = handle
  },
  setTarget(tool: Tool | null): void {
    hud?.setTarget(tool)
  },
  setCard(card: HudCard | null): void {
    hud?.setCard(card)
  },
  setStatus(parts: Array<[string, string]>): void {
    hud?.setStatus(parts)
  },
}

export const openButtonBridge = {
  bind(handle: OpenButtonHandle | null): void {
    openButton = handle
  },
  show(x: number, y: number): void {
    openButton?.show(x, y)
  },
  hide(): void {
    openButton?.hide()
  },
  /** What a press does - set by the composition root (open the target tool). */
  onPress: null as (() => void) | null,
}
